import React from 'react';
import classNames from 'classnames';
import { Stack } from '@moda/om';
import { formatPrice } from '@moda/portal-stanchions';
import { ProductDetailsFragment } from '../../generated/types';
import { EstimatedVatLink } from '../EstimatedVatLink';
import { getRoundedMarkdownPercent } from '../../lib/variantUtils/getRoundedMarkdownPercent';
import { useCountry } from '../CountrySelector';

import './PDPProductPrice.scss';

type Price = NonNullable<
  NonNullable<ProductDetailsFragment['otherVariants'][number]>['prices']['currentPrice']
>;

interface Props extends React.HTMLProps<HTMLDivElement> {
  depositPrice: Price | null;
  originalPrice: Price | null;
  currentPrice: Price | null;
  vatPrice?: Price | null;
  payInFull: boolean;
}

export const PDPProductPrice: React.FC<Props> = ({
  className,
  depositPrice,
  originalPrice,
  currentPrice,
  vatPrice,
  payInFull,
  children
}) => {
  const markdownPercent = getRoundedMarkdownPercent({
    currentPrice: Number(currentPrice?.price || '0.0'),
    originalPrice: Number(originalPrice?.price || '0.0')
  });

  const {
    country: { currencyCode }
  } = useCountry();

  return (
    <Stack className={classNames('PDPProductPrice', className)} space={1}>
      <Stack alignItems="center" direction="horizontal" justifyContent="center" space={1}>
        {originalPrice &&
          currentPrice &&
          Number(originalPrice.price) !== Number(currentPrice.price) && (
            <div className="PDPProductPrice__original-price">
              {formatPrice(originalPrice.currency, originalPrice.price, { overrideFraction: true })}
            </div>
          )}

        {currentPrice && (
          <span className="PDPProductPrice__current-price">
            {formatPrice(currentPrice.currency, currentPrice.price, { overrideFraction: true })}
          </span>
        )}

        {depositPrice?.price && !payInFull && (
          <span className="PDPProductPrice__deposit">
            {`(${formatPrice(depositPrice.currency, depositPrice.price)} Deposit)`}
          </span>
        )}

        {markdownPercent > 0 && (
          <span className="PDPProductPrice__markdown-percent">Save {markdownPercent}%</span>
        )}
      </Stack>

      {children}

      {vatPrice && currentPrice && (currencyCode === 'GBP' || currencyCode === 'EUR') && (
        <EstimatedVatLink currentPrice={currentPrice} vatPrice={vatPrice} />
      )}
    </Stack>
  );
};
